import { useNavigation } from '@react-navigation/native';
import { BaseText, Button } from 'aimo-ui';
import CancelTermination from 'components/park/evcharge/terminate/ChargingStopCancel';
import StopParkingCharging from 'components/park/evcharge/terminate/StopParkingCharging';
import useMinimumLoadingTime from 'hooks/useMinimumLoadingTime';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useStopChargingMutation from 'hooks/useStopChargingMutation';
import useUpdateParkingMutation from 'hooks/useUpdateParkingMutation';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { RootNavProps } from 'types/navigation';
import { createAction } from 'utils/analytics';
import { chargingAllowedToStop } from 'utils/chargingUtils';
import Logger, { UserActionType, UserFlowName } from 'utils/Logger';
import { testIdentifiers } from 'utils/testIdentifiers';

const { width } = Dimensions.get('window');

const StopParkingConfirm: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const { ongoingChargingSession } = useOngoingChargingSession();

  const chargingZone = ongoingChargingSession?.parkingZoneUid;

  const callback = (sessionIdString: string) => {
    navigate('parkingReceipt', {
      sessionId: sessionIdString,
      chargingSessionId: getChargingSessionId(),
    });
  };
  const { sessionId, parkZoneUid, vehicleLabel } = useParkingStore(
    (state) => state.ongoingParkingSession
  );

  const getChargingSessionId = () => {
    if (chargingCanBeStopped) {
      return ongoingChargingSession?.chargingSessionId ?? '';
    }
  };
  const hasCharging = parkZoneUid === chargingZone;
  const chargingCanBeStopped = chargingAllowedToStop(
    ongoingChargingSession?.status ?? 'IN_PROGRESS'
  );
  const { updateParking, updateParkingLoading } = useUpdateParkingMutation({
    callback,
    sessionId,
    actionType: 'STOP',
    isModal: true,
  });
  const { stopChargingSessionV2, loading: stopChargingLoading } =
    useStopChargingMutation();

  const handleStopParkingAndChargingSessions = async () => {
    stopChargingSessionV2();
    updateParking();
    createAction('TAP', 'END_PARK_AND_CHARGE_SESSION');
  };

  const handleStopParking = async () => {
    Logger.logUserAction(UserFlowName.EndParkingSession, UserActionType.Tap);
    updateParking();
  };

  const continueParking = async () => {
    stopChargingSessionV2();
    createAction('TAP', 'END_CHARGING_SESSION');
  };

  const { showLoading } = useMinimumLoadingTime(
    updateParkingLoading || stopChargingLoading,
    5000
  );

  const stopBoth = hasCharging && chargingCanBeStopped;

  const renderContent = () => {
    if (hasCharging && chargingCanBeStopped) {
      return (
        <StopParkingCharging
          handleStopParkingAndChargingSessions={
            handleStopParkingAndChargingSessions
          }
          continueParking={continueParking}
          loading={showLoading}
        />
      );
    }
    return (
      <>
        <ColumnWithMargins mb={30}>
          <BaseText testID="stop-ongoing-parking-confirm-description">
            {t('parking.stopParkingConfirmDescription', { vehicleLabel })}
          </BaseText>
        </ColumnWithMargins>
        <StyledWrapper center maxWidth={width} mb={20}>
          <Button
            size="l"
            {...testIdentifiers('stop-ongoing-parking-button')}
            text={t(
              updateParkingLoading
                ? 'parking.stoppingParking'
                : 'parking.stopParking'
            )}
            onPress={handleStopParking}
            loading={showLoading}
          />
        </StyledWrapper>
      </>
    );
  };

  return (
    <>
      {renderContent()}
      <CancelTermination
        textKey={
          stopBoth ? 'evCharging.continueChargingParking' : 'button.cancel'
        }
        testID="cancel-stop-ongoing-parking"
        terminateBoth={stopBoth}
      />
    </>
  );
};

export default StopParkingConfirm;
